import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Trash2, FileText, Calendar, Loader2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { useToast } from '@/hooks/use-toast';
import { useClient } from '../../contexts/ClientContext';
import { getApiUrl } from '../../utils/config';

interface MediaFile {
  id: string;
  original_filename: string;
  title?: string;
}

interface PostDependency {
  id: string;
  title: string;
  status: string;
  post_group_id?: string;
  created_at: string;
}

interface PostGroupDependency {
  id: string;
  title: string;
  status: string;
  created_at: string;
}

interface MediaDependencies {
  file_id: string;
  has_dependencies: boolean;
  dependencies: {
    posts: PostDependency[];
    post_groups: PostGroupDependency[];
  };
}

interface MediaDeleteConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (files: MediaFile[], forceDelete: boolean) => void;
  files: MediaFile[];
  isDeleting: boolean;
}

const MediaDeleteConfirmDialog: React.FC<MediaDeleteConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  files,
  isDeleting
}) => {
  const [dependencies, setDependencies] = useState<Record<string, MediaDependencies>>({});
  const [loadingDependencies, setLoadingDependencies] = useState(false);
  const [showForceOption, setShowForceOption] = useState(false);
  const { currentClient } = useClient();
  const { toast } = useToast();

  // Check dependencies when dialog opens
  useEffect(() => {
    if (isOpen && files.length > 0 && currentClient) {
      checkDependencies();
    }
  }, [isOpen, files, currentClient]);

  const checkDependencies = async () => {
    if (!currentClient) return;

    setLoadingDependencies(true);
    const dependencyResults: Record<string, MediaDependencies> = {};

    try {
      // Check dependencies for each file
      await Promise.all(
        files.map(async (file) => {
          try {
            const response = await fetch(`${getApiUrl()}/api/v1/media/${file.id}/dependencies?t=${Date.now()}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                'X-Client-ID': currentClient.id,
                'Cache-Control': 'no-cache',
              },
            });

            if (response.ok) {
              const data = await response.json();
              dependencyResults[file.id] = data;
            }
          } catch (error) {
            console.error(`Error checking dependencies for file ${file.id}:`, error);
          }
        })
      );

      setDependencies(dependencyResults);

      // Show force option if any file has dependencies
      const hasDependencies = Object.values(dependencyResults).some(dep => dep.has_dependencies);
      setShowForceOption(hasDependencies);
    } catch (error) {
      console.error('Error checking dependencies:', error);
      toast({
        title: "Error",
        description: "Failed to check file dependencies. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoadingDependencies(false);
    }
  };

  const getTotalDependencies = () => {
    let totalPosts = 0;
    let totalPostGroups = 0;

    Object.values(dependencies).forEach(dep => {
      totalPosts += dep.dependencies.posts.length;
      totalPostGroups += dep.dependencies.post_groups.length;
    });

    return { totalPosts, totalPostGroups };
  };

  const handleDelete = (forceDelete: boolean = false) => {
    onConfirm(files, forceDelete);
  };

  const { totalPosts, totalPostGroups } = getTotalDependencies();
  const hasAnyDependencies = totalPosts > 0 || totalPostGroups > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-slate-200/50 dark:border-slate-700/50 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-3 text-slate-900 dark:text-slate-100">
            <div className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-500" />
            </div>
            <span>Delete Media File{files.length > 1 ? 's' : ''}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Loading Dependencies */}
          {loadingDependencies && (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center space-x-3">
                <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
                <span className="text-slate-600 dark:text-slate-400">Checking file dependencies...</span>
              </div>
            </div>
          )}

          {/* Files to Delete */}
          {!loadingDependencies && (
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3">
                  Files to be deleted ({files.length}):
                </h4>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {files.map((file) => (
                    <div key={file.id} className="flex items-center space-x-3 p-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                      <FileText className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-700 dark:text-slate-300 truncate">
                        {file.title || file.original_filename}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dependencies Warning */}
              {hasAnyDependencies && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl"
                >
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">
                        Warning: Dependencies Found
                      </h4>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
                        These files are being used by existing posts. Deleting them will also delete the associated content:
                      </p>
                      
                      <div className="space-y-3">
                        {totalPosts > 0 && (
                          <div className="flex items-center space-x-2 text-sm">
                            <FileText className="w-4 h-4 text-amber-600" />
                            <span className="font-medium text-amber-800 dark:text-amber-200">
                              {totalPosts} post{totalPosts > 1 ? 's' : ''} will be deleted
                            </span>
                          </div>
                        )}
                        
                        {totalPostGroups > 0 && (
                          <div className="flex items-center space-x-2 text-sm">
                            <Calendar className="w-4 h-4 text-amber-600" />
                            <span className="font-medium text-amber-800 dark:text-amber-200">
                              {totalPostGroups} post group{totalPostGroups > 1 ? 's' : ''} will be deleted
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Detailed Dependencies */}
                      <div className="mt-4 space-y-3 max-h-40 overflow-y-auto">
                        {Object.entries(dependencies).map(([fileId, dep]) => {
                          if (!dep.has_dependencies) return null;
                          
                          const file = files.find(f => f.id === fileId);
                          return (
                            <div key={fileId} className="border-l-2 border-amber-400 pl-3">
                              <p className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-1">
                                {file?.title || file?.original_filename}:
                              </p>
                              
                              {dep.dependencies.posts.map((post) => (
                                <div key={post.id} className="text-xs text-amber-700 dark:text-amber-300 ml-2">
                                  • Post: "{post.title}" ({post.status})
                                </div>
                              ))}
                              
                              {dep.dependencies.post_groups.map((group) => (
                                <div key={group.id} className="text-xs text-amber-700 dark:text-amber-300 ml-2">
                                  • Group: "{group.title}" ({group.status})
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* No Dependencies */}
              {!hasAnyDependencies && !loadingDependencies && (
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <X className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-800 dark:text-green-200">Safe to Delete</h4>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        No posts or content are using these files.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <div className="flex items-center space-x-3 w-full">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            
            {hasAnyDependencies ? (
              <Button
                onClick={() => handleDelete(true)}
                variant="destructive"
                className="flex-1 bg-red-500 hover:bg-red-600"
                disabled={isDeleting || loadingDependencies}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete All ({files.length + totalPosts + totalPostGroups} items)
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={() => handleDelete(false)}
                variant="destructive"
                className="flex-1"
                disabled={isDeleting || loadingDependencies}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete {files.length > 1 ? `${files.length} Files` : 'File'}
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MediaDeleteConfirmDialog;